import { addToast, TableCollapsable, TablePagination } from '@octano/global-ui';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import TableEmptyContent from '../../../../../components/text/TableEmptyContent';
import { PathsLayouts } from '../../../../../config/routes';
import { TranslationsKeys } from '../../../../../locales/translations';
import {
  ContinuousEducationType,
  StudyPlanVersionStatus,
  updateVersionStatusRequest,
} from '../../api';
import {
  useStudyPlansListLoaderData,
  useStudyPlansListLoader,
} from '../../hooks/StudyPlansListLoader';
import { useStudyPlansColumns } from '../../hooks/useStudyPlansColumns';
import { StudyPlanDetail } from '../StudyPlanDetail';

const ITEMS_PER_PAGE = 10;

export function StudyPlansList() {
  const { t } = useTranslation(
    TranslationsKeys.CONTINUOUS_EDUCATION_STUDY_PLANS,
  );
  const prefix = 'list';
  const history = useHistory();

  const { data, total, total_pages } = useStudyPlansListLoaderData();
  const { query, updateQuery } = useStudyPlansListLoader();

  const handleStatusChange = useCallback(
    async (versionId: number, newStatus: StudyPlanVersionStatus) => {
      const { error } = await updateVersionStatusRequest(versionId, newStatus);
      if (error) {
        addToast({
          icon: 'error',
          color: 'danger',
          text: t(`common.msg.statusChangeError`),
          className: 'long-toast',
        });
        return;
      }
      updateQuery({ ...query });
    },
    [t, query, updateQuery],
  );

  const goToManage = useCallback(
    (id: number, type: string | undefined) => {
      const path =
        type === ContinuousEducationType.COURSE
          ? `${PathsLayouts.continuousEducation}/study-plans/course/${id}`
          : `${PathsLayouts.continuousEducation}/study-plans/program-course/${id}`;
      history.push(path);
    },
    [history],
  );

  const columns = useStudyPlansColumns({
    onStatusChange: handleStatusChange,
    onManage: goToManage,
  });

  return (
    <div id={'curriculum-mention-list-table'}>
      <TableCollapsable
        columns={columns}
        data={data}
        striped
        isStartCollapsed={() => false}
        noResultsText={
          <TableEmptyContent
            title={t(`${prefix}.emptyTitle`)}
            subtitle={t(`${prefix}.emptySubtitle`)}
          />
        }
        secondColumn={{
          columnName: 'detail',
          headerText: t(`${prefix}.detail`),
          cellFormat: ({ row }) => <StudyPlanDetail studyPlan={row} />,
        }}
      />
      {total > ITEMS_PER_PAGE && (
        <TablePagination
          pagination={{
            currentPage: query.page + 1,
            totalItems: total,
            totalPages: total_pages,
            itemsPerPage: ITEMS_PER_PAGE,
            onChangePage: (page: number) =>
              updateQuery({ ...query, page: page - 1 }),
          }}
        />
      )}
    </div>
  );
}
